/**
 * <CountUp end={42} /> — animates a number from 0 → `end` on mount and on
 * every change of `end`. Pure DOM writes inside requestAnimationFrame (no
 * setState during the tween) so it sidesteps React's
 * `react-hooks/set-state-in-effect` rule and stays GC-friendly for
 * high-frequency frames.
 *
 * Props:
 *   end       — target number (or `null` / `undefined` while loading).
 *   duration  — ms · default 600 · easeOutCubic.
 *   format    — (n: number) => string · default toLocaleString() (digit-grouped).
 *
 * Originally lived inside PrivacyGlanceWidget; promoted here so other
 * pages (Security · marketing landing · admin dashboards) can reuse it
 * without copy-paste.
 */
import React, { useEffect, useRef } from 'react';

const defaultFormat = (n) => Math.round(n).toLocaleString();

export default function CountUp({ end, format = defaultFormat, duration = 600 }) {
  const spanRef = useRef(null);
  const lastEndRef = useRef(null);

  useEffect(() => {
    if (typeof end !== 'number') return undefined;
    if (lastEndRef.current === end) return undefined;
    lastEndRef.current = end;

    const node = spanRef.current;
    if (!node) return undefined;

    const reduce = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce || end <= 0) {
      node.textContent = format(end || 0);
      return undefined;
    }

    let raf = 0;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // cubic ease-out
      const next = p < 1 ? end * eased : end;
      node.textContent = format(next);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [end, duration, format]);

  // SSR / first paint shows the formatted end value so the layout doesn't
  // jump if the animation never starts (no-JS / reduced-motion).
  return <span ref={spanRef}>{format(typeof end === 'number' ? end : 0)}</span>;
}
